'use client'

import { HotspotCard } from '@/components/Hotspot'
import LoadingDisplay from '@/components/LoadingDisplay'
import CameraSetup from '@/components/XR/CameraSetup'
import Controls from '@/components/XR/Controls'
import UserInstructionTooltip from '@/components/XR/UserInstructionTooltip'
import VideoSphere from '@/components/XR/VideoSphere'
import { useUserPrefs } from '@/context/UserPrefsContext'
import { useVideo } from '@/context/VideoContext'
import { Preload } from '@react-three/drei'
import { Canvas } from '@react-three/fiber'
import { useEffect, useState } from 'react'
import * as THREE from 'three'
import HotspotController, { HOTSPOT_STATE_EVENT, type HotspotStateEvent } from './HotspotController'

// Only import debug components in development
const DebugClickPosition =
  process.env.NODE_ENV === 'development' ? require('@/components/Debug/DebugClickPosition').default : () => null

export default function XRCanvas() {
  const [isClient, setIsClient] = useState(false)
  const { videoReady } = useVideo()
  const { userPrefs } = useUserPrefs()
  const [hotspotState, setHotspotState] = useState<HotspotStateEvent['detail'] | null>(null)

  useEffect(() => {
    setIsClient(true)
  }, [])

  // Listen for hotspot state changes
  useEffect(() => {
    const handleHotspotStateChange = (event: Event) => {
      const customEvent = event as HotspotStateEvent
      setHotspotState(customEvent.detail)
    }

    window.addEventListener(HOTSPOT_STATE_EVENT, handleHotspotStateChange)

    return () => {
      window.removeEventListener(HOTSPOT_STATE_EVENT, handleHotspotStateChange)
    }
  }, [])

  if (!isClient) {
    return <LoadingDisplay message="Initializing 360° video experience..." size="large" fullScreen />
  }

  return (
    <div style={{ width: '100%', height: '100svh', overflow: 'hidden', position: 'relative' }}>
      {!videoReady && (
        <div style={{ position: 'absolute', inset: 0, zIndex: 10 }}>
          <LoadingDisplay message="Loading video experience..." size="large" fullScreen />
        </div>
      )}
      <div
        style={{
          width: '100%',
          height: '100%',
          opacity: videoReady ? 1 : 0,
          pointerEvents: videoReady ? 'auto' : 'none',
        }}
      >
        <Canvas
          gl={{
            antialias: false,
            powerPreference: 'high-performance',
            preserveDrawingBuffer: false,
          }}
          dpr={[1, 1.5]}
          flat
          frameloop="always"
          onCreated={({ gl }) => {
            gl.outputColorSpace = THREE.SRGBColorSpace
            if (gl.shadowMap) gl.shadowMap.enabled = false
          }}
        >
          <CameraSetup />
          <Controls />
          <VideoSphere />
          <HotspotController />
          <Preload all />
          {userPrefs.debugMode && <DebugClickPosition />}
        </Canvas>
      </div>

      <div style={{ position: 'absolute', inset: 0, zIndex: 20, pointerEvents: 'none' }}>
        <UserInstructionTooltip />
      </div>

      {/* Render hotspot card outside Three.js Canvas to preserve React context */}
      {hotspotState?.hotspot && (
        <HotspotCard
          data={hotspotState.hotspot}
          onClose={hotspotState.onClose}
          isClosing={hotspotState.isClosing}
          isOpening={false}
        />
      )}
    </div>
  )
}
